import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { BaseChildState } from '@stateMachine/BaseChildState'
import {
    getLadderRemainingCoinCapacity,
    readLadderCurrentJackpot,
    resolveReachedLadderMilestone,
} from '@/gameConfigs/components/JackpotLaddersComponent'
import { getFreeSpinsFinalWinAmount, isLastFreeSpinRound } from '@/states/freespins/FreeSpinsFinalWin'
import { WinningUtil } from '@/managers/winning/WinningUtil'

export class FsCoinCollectState extends BaseChildState {
    private static readonly COLLECT_FALLBACK_MS: number = 8000

    private _waitingCollectCompletion: boolean = false
    private _waitingUpgrade: boolean = false
    private _waitingJackpotPopup: boolean = false
    private _fallbackTimeoutId: number = null

    public canTrigger(): boolean {
        if ((Facade as any).skipFinalWinPresentationAfterHoldAndWin) {
            return false
        }

        const isFreeSpins = Boolean(this._model.roundState?.isFreeSpins)
        const isHoldAndWinActive = Boolean(this._model.spinState?.isHoldAndWinActive)
        const nextRoundType = String((this._model.spinData as any)?.next_round_type || '').toLowerCase()
        const nextIsRespin = nextRoundType === 'respin' || nextRoundType === 'respins'
        // 6+ coins during FS enter Hold & Win — skip jackpot-ladder collect flies.
        const coinCount = Number(
            (this._model.spinData as any)?.bonus?.num_coin_symbols ??
                (this._model.spinData as any)?.hold_and_win_extra?.num_coin_symbols ??
                0
        )
        const triggersHoldAndWin = Number.isFinite(coinCount) && coinCount >= 6
        if (!isFreeSpins || isHoldAndWinActive || nextIsRespin || triggersHoldAndWin) {
            return false
        }

        // After every level is unlocked, later FS spins must not collect coins. The spin that
        // fills the last level still runs — it has capacity left — so the Grand upgrade can play.
        if (getLadderRemainingCoinCapacity() <= 0 && !isLastFreeSpinRound(this._model)) {
            return false
        }

        return true
    }

    public begin() {
        ;(Facade as any).isFsCollectPresentationActive = true
        GameSignals.stopButtonClicked.connect(this.onSkipPresentation)
        GameSignals.spinButtonClicked.connect(this.onSkipPresentation)
        this._waitingCollectCompletion = true
        ;(GameSignals as any).fsCollectSequenceComplete?.connect(this.onCollectSequenceComplete)
        ;(GameSignals as any).fsCollectSequenceRequested?.emit()
        this._fallbackTimeoutId = window.setTimeout(this.onCollectFallback, FsCoinCollectState.COLLECT_FALLBACK_MS)
    }

    private clearCollectFallback(): void {
        if (this._fallbackTimeoutId === null) {
            return
        }

        clearTimeout(this._fallbackTimeoutId)
        this._fallbackTimeoutId = null
    }

    private onCollectFallback = () => {
        this._fallbackTimeoutId = null
        if (!this._waitingCollectCompletion) {
            return
        }

        ;(GameSignals as any).fsCollectSequenceForceComplete?.emit()
        if (this._waitingCollectCompletion) {
            this.onCollectSequenceComplete()
        }
    }

    private onCollectSequenceComplete = () => {
        if (!this._waitingCollectCompletion) {
            return
        }

        this._waitingCollectCompletion = false
        this.clearCollectFallback()
        ;(GameSignals as any).fsCollectSequenceComplete?.disconnect(this.onCollectSequenceComplete)
        this.syncFinalFreeSpinsWin()

        // Every coin has reached the ladder. If that completed a milestone, the Jackpot
        // Upgrade plays before the round moves on.
        const reachedLevelIndex = resolveReachedLadderMilestone()
        if (reachedLevelIndex === null) {
            this.playJackpotPopupOrEnd()
            return
        }

        this.playJackpotUpgrade(reachedLevelIndex)
    }

    private syncFinalFreeSpinsWin(): void {
        if (!isLastFreeSpinRound(this._model)) {
            return
        }

        // Only prepare BigWin level here — Win Meter is credited when the FS total-win popup opens.
        const finalWinAmount = getFreeSpinsFinalWinAmount(this._model)
        this._model.bigWinLevel = WinningUtil.getBigWinLevel(finalWinAmount, this._model.currentBaseBet)
    }

    private playJackpotUpgrade(reachedLevelIndex: number): void {
        this._waitingUpgrade = true

        // Re-block input for the presentation: the collect sequence released it on completion.
        Facade.isPopupBlockingInput = true

        ;(GameSignals as any).jackpotLadderUpgradeComplete?.connect(this.onUpgradeComplete)

        // Emitted last: the component can answer synchronously (missing spine, or an
        // overlapping start), which would otherwise end this state before the listener and
        // connection above were in place, leaking both.
        ;(GameSignals as any).jackpotLadderUpgradeStart?.emit(reachedLevelIndex)
    }

    private onSkipPresentation = () => {
        if (this._waitingCollectCompletion) {
            ;(GameSignals as any).fsCollectSequenceSkip?.emit()
            return
        }

        if (this._waitingUpgrade) {
            ;(GameSignals as any).jackpotLadderUpgradeSkip?.emit()
        }
    }

    private onUpgradeComplete = () => {
        if (!this._waitingUpgrade) {
            return
        }

        this._waitingUpgrade = false
        this.clearUpgradeWait()
        Facade.isPopupBlockingInput = false
        this.playJackpotPopupOrEnd()
    }

    /**
     * On the last free spin the player is shown the jackpot they finished the feature holding,
     * with its prize. Runs after the upgrade so a spin that both crosses a milestone and ends
     * the feature shows the upgrade first, then the jackpot it upgraded into.
     */
    private playJackpotPopupOrEnd(): void {
        ;(Facade as any).isFsCollectPresentationActive = false

        if (!this.isLastFreeSpin()) {
            this.end()
            return
        }

        //## Null while the ladder marker is still on a cash step: no jackpot to award.
        const jackpot = readLadderCurrentJackpot()
        if (!jackpot) {
            this.end()
            return
        }

        this._waitingJackpotPopup = true

        //## Connected before the emit: the component answers synchronously when it has no
        //## spine or is already playing, which would otherwise end the state first.
        ;(GameSignals as any).jackpotPopupComplete?.connect(this.onJackpotPopupComplete)
        ;(GameSignals as any).jackpotPopupStart?.emit([{ tier: jackpot.tier, amount: jackpot.prizeText }])
    }

    private onJackpotPopupComplete = () => {
        if (!this._waitingJackpotPopup) {
            return
        }

        this._waitingJackpotPopup = false
        this.clearJackpotPopupWait()
        this.end()
    }

    private clearJackpotPopupWait(): void {
        ;(GameSignals as any).jackpotPopupComplete?.disconnect(this.onJackpotPopupComplete)
    }

    private isLastFreeSpin(): boolean {
        const amountLeft = Number((this._model.spinData as any)?.freebet?.amount_left ?? -1)
        return Number.isFinite(amountLeft) && amountLeft === 0
    }

    private clearUpgradeWait(): void {
        ;(GameSignals as any).jackpotLadderUpgradeComplete?.disconnect(this.onUpgradeComplete)
    }

    public cleanUp() {
        ;(Facade as any).isFsCollectPresentationActive = false
        GameSignals.stopButtonClicked.disconnect(this.onSkipPresentation)
        GameSignals.spinButtonClicked.disconnect(this.onSkipPresentation)
        this._waitingCollectCompletion = false
        this.clearCollectFallback()
        ;(GameSignals as any).fsCollectSequenceComplete?.disconnect(this.onCollectSequenceComplete)

        if (this._waitingUpgrade) {
            this._waitingUpgrade = false
            Facade.isPopupBlockingInput = false
        }

        this._waitingJackpotPopup = false
        this.clearUpgradeWait()
        this.clearJackpotPopupWait()
    }
}
